// Sentry reporting for the quote flow. The flow's own catches decide
// what's an expected refusal (a 4xx the UI already has a branch for) —
// this module only decides, given that a failure reached it at all,
// whether it's a bug worth an engineer's attention.

import * as Sentry from "@sentry/react";

import { NetworkError } from "hooks/usePublicAPI";

import type { QuoteType } from "./analytics";

interface FailureScope {
  operation: string;
  quoteId: string | null;
}

/**
 * Reports a quote-flow failure to Sentry unless it's an expected refusal
 * (401/402/404/409/422) with its own UI branch — those are business
 * states, not bugs, and reporting them would bury the failures that need
 * a fix. Returns whether it reported.
 */
export function reportQuoteFlowFailure(
  error: unknown,
  { operation, quoteId }: FailureScope,
): boolean {
  if (error instanceof NetworkError && error.statusCode < 500) {
    return false;
  }

  Sentry.captureException(error, {
    tags: { quote_id: quoteId ?? "unknown", operation },
    extra: {
      status: error instanceof NetworkError ? error.statusCode : null,
    },
  });
  return true;
}

/** Stamps every later Sentry event in this tab with the quote it's
 * scoped to. Called once by QuoteFlow on mount. */
export function tagQuoteFlowScope(quoteId: string, quoteType: QuoteType): void {
  Sentry.setTag("quote_id", quoteId);
  Sentry.setContext("quote_flow", { quote_id: quoteId, quote_type: quoteType });
}
